import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button.js';
import { ProcessMenu } from '../components/ProcessMenu.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { useCustomer } from '../customer-context.js';
import { api } from '../api/client.js';
import type { components } from '../api/schema.js';
import { AuthHoldPopup, type AuthHoldCheckResult } from './AuthHoldPopup.js';
import { getJnHoldBlock, setJnHoldBlock } from '../jn-hold-block-store.js';
import { evaluateJnHold, type JnHoldResult } from '../lib/jn-hold.js';
import { holdCommentLabel, holdLabel } from '../lib/hold-codes.js';
import { formatOwners } from '../lib/owners.js';
import { accountName } from '../lib/account-label.js';
import { ServicingVerificationRequired } from '../components/ServicingVerificationGuard.js';
import infoIcon from '../assets/info-icon.svg';

/** Formats an ISO date of birth (YYYY-MM-DD) as DD/MM/YYYY for display. */
function formatDob(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/**
 * Route an account tile to its drill-down screen by account type (CCF-80):
 * CURRENT/savings → SCRN-45, DEPOSIT → SCRN-52, LOAN → SCRN-44.
 *
 * Credit-card accounts (`CARD`, gateway ProductType `CC`) have NO drill-down
 * (PTSB / Dylan, issue #75): returns `null` so the tile is inert — clicking it
 * does nothing (no navigation, no error).
 */
function detailPathFor(accountType: string): string | null {
  switch (accountType) {
    case 'CARD':
      // Credit card — no detail screen; the tile must not navigate (issue #75).
      return null;
    case 'DEPOSIT':
      return '/accounts/deposit';
    case 'LOAN':
      return '/accounts/term-loan';
    default:
      // CURRENT and any savings/other type fall through to the general
      // Account Information overview (SCRN-45).
      return '/accounts/information';
  }
}

/**
 * Raw account tile as returned by the dashboard BFF (`customer/accounts/list`).
 *
 * The first block is the field set the gateway emits today. The CCF-80 tile also
 * surfaces BIC / IBAN / card number / Owners / Signatories — these are described
 * as OPTIONAL fields on the `Account` schema in `app/openapi.yaml` (the
 * contract-first source of truth), typed optional here and rendered defensively
 * (omitted/placeholdered when absent). They appear automatically once the
 * `accounts/list` projection carries them. `CardNumber` is always a MASKED
 * display value (`•••• 1234`), never a full PAN.
 */
interface DashboardAccount {
  AccountNumber: string;
  AccountType: 'CURRENT' | 'DEPOSIT' | 'LOAN' | 'CARD' | string;
  /**
   * The raw gateway `ProductType` for the account (e.g. CAA, SBA, TDA, LAA,
   * HLA, PEP, CC), carried through verbatim by the BFF adapter. Used to GROUP
   * the dashboard tiles by product type in the fixed PTSB order (issue #110) —
   * this is finer-grained than the collapsed `AccountType` bucket (e.g. SBA and
   * TDA both map to DEPOSIT but are distinct product groups). Optional/read
   * defensively: when absent the account sorts after the known groups, keeping
   * its gateway order.
   */
  GatewayProductType?: string;
  Nickname: string;
  Balance: number;
  AvailableBalance: number;
  Currency: string;
  Status: 'ACTIVE' | 'DORMANT' | 'CLOSED' | 'CANCELLED' | string;
  HoldCount: number;
  /** Bank Identifier Code (CCF-80 tile field). */
  Bic?: string;
  /** IBAN / policy number — the account's primary association identifier. */
  Iban?: string;
  /**
   * Debit/deposit/credit card number for the relevant tile, sourced from the
   * gateway's `DebitCardNumber` field. Rendered in FULL plain text on the
   * dashboard tile — an explicit security sign-off scoped to the dashboard page
   * only (dylan219, issue #97). Other surfaces still mask it.
   */
  CardNumber?: string;
  /** Names of the account holders (single or joint). */
  Owners?: string;
  /** Number of signatories required to transact on the account. */
  Signatories?: string | number;
  /**
   * Holds present on the account (CCF-83). Each carries the hold Code shown on
   * the tile and an optional Comment revealed when the agent expands the tile
   * via "View Holds". Optional/rendered defensively — the count (`HoldCount`)
   * is always present; the code/comment list lights up when the projection
   * carries it.
   */
  Holds?: Array<{ Code?: string; Comments?: string; Reason?: string }>;
}

/**
 * Default view = ACTIVE accounts only (issue #420, Dylan 2026-07-17 authoritative
 * ruling: "only active accounts are displayed until the box 'show all
 * associations' is ticked"). The dashboard shows ONLY accounts whose gateway
 * `Status` is ACTIVE until the agent opts in via the "Show all associations"
 * toggle, at which point every association (Inactive / Dormant / Closed /
 * Cancelled / …) is revealed.
 *
 * This is a POSITIVE (allow-list) filter, deliberately not a negative "hide
 * Closed/Cancelled" one: the real gateway can return an INACTIVE (or other
 * non-Active) association even with `ShowInactive:"N"` — which is exactly how the
 * INACTIVE "FlexiSaver - Demand" leaked into the default view in #420 — so the
 * client must gate on ACTIVE positively rather than enumerating the statuses to
 * hide. Complements (does not replace) the #194 server-side `ShowInactive`
 * re-fetch; this is the belt to that braces.
 */
function isActiveAccount(account: DashboardAccount): boolean {
  return String(account.Status).toUpperCase() === 'ACTIVE';
}

/**
 * Fixed dashboard tile ordering by gateway `ProductType` (PTSB / Dylan, issue
 * #110). The tiles are grouped first-to-last in this exact sequence:
 *
 *   1. Current Account   — CAA
 *   2. Savings Account   — SBA
 *   3. Deposit Accounts  — TDA
 *   4. Term Loans        — LAA
 *   5. Mortgages         — HLA & PEP
 *   6. Credit Cards      — CC
 *
 * This replaces the earlier "preferred account first" request, which Dylan
 * dropped because the accounts-list projection carries no preferred-account
 * indicator. Ordering is keyed on the raw `GatewayProductType` (not the
 * collapsed `AccountType` bucket) because Savings (SBA) and Deposit (TDA) share
 * the DEPOSIT bucket yet are distinct groups here.
 */
const PRODUCT_TYPE_ORDER: ReadonlyArray<string> = [
  'CAA',
  'SBA',
  'TDA',
  'LAA',
  'HLA',
  'PEP',
  'CC',
];

/**
 * Rank an account by its gateway `ProductType` for the fixed dashboard order
 * (issue #110). A recognised product type returns its position in
 * {@link PRODUCT_TYPE_ORDER}; an absent/unknown type ranks last so it falls
 * after the known groups (keeping its incoming gateway order via the stable
 * sort below).
 */
function productTypeRank(account: DashboardAccount): number {
  const rank = PRODUCT_TYPE_ORDER.indexOf(
    String(account.GatewayProductType ?? '').toUpperCase(),
  );
  return rank === -1 ? PRODUCT_TYPE_ORDER.length : rank;
}

/**
 * Group/order the account tiles by product type in the fixed PTSB sequence
 * (issue #110), preserving the gateway's order WITHIN each group. Uses a stable
 * sort keyed on {@link productTypeRank}, so accounts of the same product type
 * stay in their original relative order.
 */
function orderByProductType(
  accounts: ReadonlyArray<DashboardAccount>,
): DashboardAccount[] {
  // Decorate-sort-undecorate to keep the sort stable on every engine and avoid
  // mutating the source array.
  return accounts
    .map((account, index) => ({ account, index }))
    .sort((a, b) => {
      const byType = productTypeRank(a.account) - productTypeRank(b.account);
      return byType !== 0 ? byType : a.index - b.index;
    })
    .map((entry) => entry.account);
}

/** Customer info block returned alongside the account tiles. */
interface DashboardInfo {
  CustomerNumber?: string;
  CustomerName?: string;
  Dob?: string;
  /** Normalised routing segment (EDS/BUS24/VISA) — drives ID&V routing, NOT the display tile. */
  Segment?: string;
  /**
   * #770 — the raw customer type / `CustDescription` ("Personal"/"Signatory")
   * from customer/identification. Shown on the "Customer type" tile, distinct
   * from the normalised routing `Segment`.
   */
  CustomerType?: string;
  /** Collapsed compliance status for the CCF-80 tile (e.g. "Complete"). */
  CustomerCompliance?: string;
  CustomerVerification?: string;
  AssistanceRequired?: string;
  CliMatch?: string;
  CallsInLastWeek?: number;
  AccountCount?: number;
}

/**
 * A customer "other product" tile (issue #362), sourced from
 * `customer/products/list`. Per issue #379 (dylan219) Visa credit cards,
 * mortgages, Insurance (`A90`) and Investment (`ILB`) are displayed here; the
 * only product type the gateway returns that is filtered out is Customer
 * Messages (`NOTE`) — see `orderProducts`. (dylan219 first ruled option A —
 * Visa + mortgages only — then updated it to also include Insurance +
 * Investment, excluding only the messages.) These do NOT appear on
 * `customer/accounts/list`, so without this the dashboard was missing them
 * entirely (dylan219). The gateway
 * masks the Visa PAN in `ProductID` itself, so it is rendered verbatim (never a
 * full PAN).
 */
// Sourced directly from the generated OpenAPI client so the SPA and the
// contract cannot drift: `ProductStatus` (issue #571) and every product field
// are inferred from the `Product` schema in `app/openapi.yaml`. A contract
// change surfaces here as a compile error. The `Product` schema is an open
// object (`additionalProperties: true`); the fields the SPA consumes are typed.
type DashboardProduct = components['schemas']['Product'];

/** Whether a product tile shows the Active (green) or Inactive (red) badge (issue #571). */
function isActiveProduct(product: DashboardProduct): boolean {
  // Default to Active when the status is absent (a pre-#571 response, or the
  // default active-only call). Only an explicit `'O'` marks a product inactive.
  return String(product.ProductStatus ?? 'A').toUpperCase() !== 'O';
}

/**
 * Human-readable heading for a product-type code (issue #362). Falls back to
 * the raw code when unmapped so a new gateway product type still renders (never
 * hidden). "Note" is the customer-messages product; a plain description is used.
 */
const PRODUCT_TYPE_LABEL: Record<string, string> = {
  VISA: 'Visa Card',
  MLS: 'Mortgage',
  HLA: 'Mortgage',
  PEP: 'Mortgage',
  NOTE: 'Customer Messages',
  A90: 'Insurance',
  ILB: 'Investment',
};

/**
 * The product-type codes shown in the "Other products" section, in tile order
 * (issue #379, dylan219): Visa Cards first, then mortgages (MLS/HLA/PEP), then
 * Insurance (`A90`) and Investment (`ILB`). Customer Messages (`NOTE`) and any
 * unmapped type are intentionally hidden here. (issue #362 originally surfaced
 * all types; #379 first narrowed that to Visa + Mortgages only [option A], then
 * dylan219 updated it to also include Insurance + Investment, excluding only the
 * Customer Messages.) Doubles as the allow-list and the tile-ordering key.
 */
const PRODUCT_TILE_ORDER: ReadonlyArray<string> = [
  'VISA',
  'MLS',
  'HLA',
  'PEP',
  'A90',
  'ILB',
];

function productTypeCode(product: DashboardProduct): string {
  return String(product.ProductType ?? '').toUpperCase();
}

function isDisplayedProductType(product: DashboardProduct): boolean {
  return PRODUCT_TILE_ORDER.includes(productTypeCode(product));
}

function productRank(product: DashboardProduct): number {
  const rank = PRODUCT_TILE_ORDER.indexOf(productTypeCode(product));
  return rank === -1 ? PRODUCT_TILE_ORDER.length : rank;
}

/** Sort key placing Active products (0) ahead of Inactive ones (1) — issue #624. */
function productActiveRank(product: DashboardProduct): number {
  return isActiveProduct(product) ? 0 : 1;
}

/**
 * Filter to the displayed product types (issue #379 — Visa Cards, Mortgages,
 * Insurance and Investment; Customer Messages excluded) and order them:
 * ACTIVE associations first, then Inactive (closed/cancelled) ones (issue
 * #624), and WITHIN each active/inactive group by `PRODUCT_TILE_ORDER`,
 * preserving the gateway's order within a type via a stable
 * decorate-sort-undecorate.
 */
function orderProducts(
  products: ReadonlyArray<DashboardProduct>,
): DashboardProduct[] {
  return products
    .filter(isDisplayedProductType)
    .map((product, index) => ({ product, index }))
    .sort((a, b) => {
      // Active associations lead (issue #624); closed/cancelled follow.
      const byActive =
        productActiveRank(a.product) - productActiveRank(b.product);
      if (byActive !== 0) return byActive;
      const byRank = productRank(a.product) - productRank(b.product);
      return byRank !== 0 ? byRank : a.index - b.index;
    })
    .map((entry) => entry.product);
}

interface DashboardResponse {
  info: DashboardInfo;
  accounts: DashboardAccount[];
  products?: DashboardProduct[];
  // Issue #672: true ⇒ the customer/accounts/list leg could NOT be loaded (a
  // gateway business failure / thrown / non-2xx — e.g. the intermittent 500s
  // behind the BAU-SIT load balancer, issue #671). The accounts section then
  // shows an error + retry instead of a misleading "No accounts to display".
  accountsError?: boolean;
}

/**
 * Card-number label for a tile, by account type (CCF-80 / figma): a current
 * account shows the Debit card number, a deposit account the Deposit card
 * number, and a card/credit product the Card number.
 */
function cardNumberLabel(accountType: string): string {
  switch (accountType) {
    case 'DEPOSIT':
      return 'Deposit card number';
    case 'CARD':
    case 'CREDIT':
      return 'Card number';
    default:
      return 'Debit card number';
  }
}

/** Formats a decimal EUR amount. Negative balances render with a minus sign. */
function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

/**
 * Quick links shown above the account tiles, per the design export.
 *
 * Labels are kept aligned with the Process Menu wording (issue #620) so the
 * same action reads identically in both places: "Payments" (not "Payments and
 * transfers") and "Change Customer Details" (not "Update personal details"),
 * matching PROCESS_MENU_OPTIONS in process-menu-shared.ts.
 */
const QUICK_LINKS = [
  'Statements',
  'Card maintenance',
  'Payments',
  'Interaction history',
  'Change Customer Details',
  'Transaction history',
] as const;

/** A single account/product tile with balances and a hold indicator. */
function AccountTile({
  account,
  onOpen,
  onViewHolds,
}: {
  account: DashboardAccount;
  /** Navigate to the type-specific drill-down (CCF-80). */
  onOpen: (account: DashboardAccount) => void;
  /** Navigate to the full Account Holds screen for this account (CCF-79). */
  onViewHolds: (account: DashboardAccount) => void;
}): React.JSX.Element {
  // CCF-83: "View Holds" expands the tile inline to show the hold + comment;
  // it does NOT navigate. The full Account Holds screen (CCF-79) is reachable
  // via a secondary link when more detail (amount, placed date) is needed.
  const [holdsExpanded, setHoldsExpanded] = useState(false);
  const { customer } = useCustomer();
  // #465 (@dylan219 2026-07-22): the expanded panel must show the per-hold
  // COMMENT. The accounts-list projection the tile reads only carries the hold
  // CODES (the real gateway's `AccountHolds` code string; the free-text
  // comment lives ONLY in the `account/holds/details` op). So when the panel is
  // opened we lazily fetch the detailed holds (`POST /api/accounts/holds`, the
  // same source SCRN-42 uses) and render their comments. Falls back to the
  // projection's holds (code-only) if the fetch fails or is empty.
  const [detailHolds, setDetailHolds] = useState<Array<{
    Code?: string;
    Comments?: string;
    Reason?: string;
  }> | null>(null);
  const active = account.Status === 'ACTIVE';
  // Issue #380: the tile heading shows the FRIENDLY product name (the gateway
  // `AccountDescription`, carried as `Nickname` — e.g. "Explore Account", "21
  // Day Notice Account"), matching the Transfer Between Accounts dropdown
  // convention, rather than the generic account-type bucket ("Current
  // Account"). Falls back to the type label, then the raw code, via the shared
  // `accountName` helper (also used by the account-list dropdowns, #381).
  const label = accountName(account);
  // Credit-card accounts have no drill-down (issue #75): the whole-tile click
  // target is suppressed so clicking the tile does nothing (no nav, no error).
  const hasDrillDown = detailPathFor(account.AccountType) !== null;
  // CCF-80: balances render black for credit, red for debit (overdrawn, term
  // loan, etc.).
  const balanceClass =
    account.Balance < 0 ? 'text-[#D23A06]' : 'text-[#1d252c]';
  const availableClass =
    account.AvailableBalance < 0 ? 'text-[#D23A06]' : 'text-[#1d252c]';
  // CCF-83: holds returned for this account. Prefer the structured `Holds`
  // list (code + comment); fall back to the `HoldCount` indicator when the
  // projection carries only the count. Hold codes (e.g. 3, N, Q) show on the
  // tile; comments are revealed only when the tile is expanded.
  const projectionHolds = account.Holds ?? [];
  // #465: once the detailed holds (with per-hold comments) have been fetched
  // for the expanded panel, prefer them; until then use the code-only
  // projection so the tile's code list + count render immediately.
  const holds = detailHolds ?? projectionHolds;
  const holdCodes = projectionHolds
    .map((h) => h.Code)
    .filter((c): c is string => typeof c === 'string' && c.length > 0);
  const holdCount = account.HoldCount ?? projectionHolds.length;
  const hasHolds = holdCount > 0 || projectionHolds.length > 0;

  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';
  const accountNumber = account.AccountNumber;
  // #465: lazily fetch the per-hold comments when the panel is first opened.
  useEffect(() => {
    if (!holdsExpanded || detailHolds !== null) return;
    if (!customerNumber || !/^\d{4}$/.test(tellerId) || !accountNumber) return;
    let cancelled = false;
    void (async () => {
      try {
        const { data, error: apiError } = await api.POST('/accounts/holds', {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber, accountNumber },
        });
        if (cancelled || apiError || !data) return;
        const fetched = (data as { Holds?: unknown }).Holds;
        if (Array.isArray(fetched)) {
          setDetailHolds(
            fetched as Array<{
              Code?: string;
              Comments?: string;
              Reason?: string;
            }>,
          );
        }
      } catch {
        // Leave `detailHolds` null — the panel falls back to the code-only
        // projection (still shows the code + catalogue meaning via holdLabel).
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [holdsExpanded, detailHolds, customerNumber, tellerId, accountNumber]);
  // Issue #274: a clickable tile gets a hover affordance (raised shadow) so it
  // reads as interactive. Applied ONLY to tiles that actually drill down — the
  // credit-card (CC) tile has no drill-down (issue #75), so it must NOT gain the
  // clickable cue (that would make a non-interactive tile look clickable). The
  // whole-tile click target below already supplies `cursor:pointer` on hover.
  const hoverAffordance = hasDrillDown
    ? 'transition-shadow hover:shadow-lg'
    : '';
  return (
    <article
      className={`border-2 border-[#dad7d3] bg-white p-6 shadow-sm relative flex flex-col ${hoverAffordance}`}
      aria-label={`${label} ${account.AccountNumber}`}
    >
      {/* Whole-tile click target → the type-specific drill-down (CCF-80).
          Suppressed for credit-card accounts, which have no drill-down (issue
          #75): the tile renders but is inert — no click target, no navigation. */}
      {hasDrillDown && (
        <button
          type="button"
          onClick={() => onOpen(account)}
          aria-label={`Open ${label} ${account.AccountNumber}`}
          className="absolute inset-0 z-0 cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-[#005fcc]/30"
        />
      )}
      {/* Foreground content sits above the click target; the holds button
          re-enables its own pointer events so it is independently clickable. */}
      <div className="relative z-10 pointer-events-none flex flex-col flex-1">
        <span
          className={`absolute top-6 right-6 px-3 py-1.5 rounded-md text-[13px] font-['Arial',sans-serif] font-bold whitespace-nowrap ${
            active
              ? 'bg-[#d1fae5] text-[#065f46]'
              : 'bg-[#fee2e2] text-[#991b1b]'
          }`}
          role="status"
          aria-label={`Account status: ${account.Status}`}
        >
          {account.Status}
        </span>

        <div className="mb-4 pr-24">
          <h3 className="text-[22px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-1">
            {label}
          </h3>
          {/* Issue #11 / #380: the redundant "Account: <number>" line and a
              secondary account-name line are intentionally not rendered — the
              bold heading (now the friendly product name, #380) + IBAN already
              identify the account, so a second name line would just duplicate
              the heading. */}
          {/* CCF-80 tile sub-lines: BIC / IBAN / card number ALWAYS render on
              every tile for a consistent layout — the labelled field stays
              present and shows a blank placeholder (—) when the gateway
              projection has no value for this account (per the figma; the field
              never silently disappears). */}
          <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0">
            BIC:{' '}
            <span className="font-bold text-[#1d252c]">
              {account.Bic || '—'}
            </span>
          </p>
          <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0">
            IBAN:{' '}
            <span className="font-bold text-[#1d252c]">
              {account.Iban || '—'}
            </span>
          </p>
          <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0">
            {cardNumberLabel(account.AccountType)}:{' '}
            <span className="font-bold text-[#1d252c]">
              {/* Issue #97: the dashboard tiles show the FULL card number in
                  plain text. This is an explicit, scoped relaxation of the
                  no-full-PAN display rule — "explicitly signed off by security
                  for display on the dashboard page only" (dylan219, issue #97).
                  It applies HERE ONLY; every other surface (Card Maintenance,
                  ID&V card search) still renders the masked `•••• 1234` form.
                  Blank (—) when no card is associated with the account. */}
              {account.CardNumber || '—'}
            </span>
          </p>
        </div>

        <div className="border-b-2 border-[#dad7d3] mb-4" aria-hidden="true" />

        <div className="flex gap-4 items-start mb-4">
          <div className="flex flex-col">
            <p className="text-[12px] font-['Arial',sans-serif] text-[#6b7280] uppercase tracking-[0.3px]">
              Account Balance
            </p>
            <p
              className={`text-[20px] font-['Arial',sans-serif] font-bold ${balanceClass}`}
            >
              {formatCurrency(account.Balance, account.Currency)}
            </p>
          </div>

          <div className="w-px bg-[#dad7d3] self-stretch" aria-hidden="true" />

          <div className="flex flex-col">
            <p className="text-[12px] font-['Arial',sans-serif] text-[#6b7280] uppercase tracking-[0.3px]">
              Available Balance
            </p>
            <p
              className={`text-[20px] font-['Arial',sans-serif] font-bold ${availableClass}`}
            >
              {formatCurrency(account.AvailableBalance, account.Currency)}
            </p>
          </div>
        </div>

        {/* Owners (CCF-80 tile field). Always rendered for a consistent tile
            layout, showing a blank placeholder (—) when the gateway projection
            has no value for this account. Joint holders are comma-separated
            (issue #11). */}
        <div className="flex flex-col mb-4">
          <p className="text-[12px] font-['Arial',sans-serif] text-[#6b7280] uppercase tracking-[0.3px]">
            Owners
          </p>
          <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
            {account.Owners ? formatOwners(account.Owners) : '—'}
          </p>
        </div>

        {/* Signatories + Holds share one row (issue #11). Signatories always
            renders; the holds indicator/codes sit alongside it. */}
        <div className="mt-auto pt-2 flex flex-wrap items-start gap-x-8 gap-y-2">
          <div className="flex flex-col">
            <p className="text-[12px] font-['Arial',sans-serif] text-[#6b7280] uppercase tracking-[0.3px]">
              Signatories
            </p>
            <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
              {account.Signatories ?? '—'}
            </p>
          </div>
          {hasHolds ? (
            <div className="flex flex-col gap-2">
              {/* Hold codes shown directly on the tile (CCF-83): e.g. "Holds: 3,
                  N, Q" — no comments at this level. */}
              {holdCodes.length > 0 && (
                <p className="text-[13px] font-['Arial',sans-serif] text-[#D23A06] m-0">
                  <span className="font-bold uppercase tracking-[0.3px] text-[12px]">
                    Holds:
                  </span>{' '}
                  <span className="font-bold">{holdCodes.join(', ')}</span>
                </p>
              )}
              <button
                type="button"
                onClick={() => setHoldsExpanded((open) => !open)}
                aria-expanded={holdsExpanded}
                aria-label={`View Holds — ${holdCount} hold${holdCount === 1 ? '' : 's'} on this account`}
                className="pointer-events-auto inline-flex items-center gap-2 px-3 py-1.5 bg-[#FFF6F2] border border-[#D23A06] rounded-[4px] text-[13px] font-['Arial',sans-serif] font-bold text-[#D23A06] hover:bg-[#ffeae1] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#005fcc]/30 cursor-pointer self-start"
              >
                <img
                  src={infoIcon}
                  alt=""
                  aria-hidden="true"
                  className="w-4 h-4"
                />
                View Holds ({holdCount} hold
                {holdCount === 1 ? '' : 's'})
              </button>

              {/* CCF-83: expanding the tile reveals the hold + comment inline. */}
              {holdsExpanded && (
                <div className="pointer-events-auto mt-1 border border-[#D23A06]/40 rounded-[4px] bg-[#FFF6F2] p-3 flex flex-col gap-3">
                  {holds.length > 0 ? (
                    <ul className="list-none m-0 p-0 flex flex-col gap-3">
                      {holds.map((hold, i) => (
                        <li
                          key={`${account.AccountNumber}-hold-${i}`}
                          className="flex flex-col"
                        >
                          {/* #465 (@dylan219 2026-07-22): show the per-hold
                              COMMENT (gateway HoldDescriptionN → Comments/Reason)
                              e.g. "Hold 1 — N — FREEZE ORDER IN PLACE", not the
                              generic catalogue description (that stays in the
                              SCRN-42 reference table). #524: once the detailed
                              `account/holds/details` fetch has answered, the
                              comment renders VERBATIM — a hold with no comment
                              shows just the code ("Hold 1 — M"), never the
                              catalogue description. Only the code-only
                              accounts-list projection (before/without the
                              detail fetch, #108) keeps the catalogue label.
                              Adjusts #108 on the warning surfaces (#108/#426). */}
                          <p className="text-[13px] font-['Arial',sans-serif] text-[#1d252c] m-0">
                            <span className="font-bold">
                              Hold {i + 1}
                              {hold.Code
                                ? ` — ${
                                    detailHolds
                                      ? holdCommentLabel(
                                          hold.Code,
                                          hold.Comments ?? hold.Reason,
                                        )
                                      : holdLabel(
                                          hold.Code,
                                          hold.Comments ?? hold.Reason,
                                        )
                                  }`
                                : ''}
                            </span>
                          </p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-[13px] font-['Arial',sans-serif] text-[#6b7280] m-0">
                      {holdCount} hold{holdCount === 1 ? '' : 's'} on this
                      account. Open the full Account Holds screen for details.
                    </p>
                  )}
                  {/* Secondary link to the full holds detail screen (CCF-79). */}
                  <button
                    type="button"
                    onClick={() => onViewHolds(account)}
                    aria-label="Open the full Account Holds screen"
                    className="self-start text-[13px] font-['Arial',sans-serif] font-bold text-[#005fcc] underline hover:no-underline focus:outline-none focus-visible:ring-4 focus-visible:ring-[#005fcc]/30 cursor-pointer"
                  >
                    View full account holds
                  </button>
                </div>
              )}
            </div>
          ) : (
            <span className="text-[13px] font-['Arial',sans-serif] text-[#6b7280]">
              No holds
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

/**
 * A single "other product" tile (issue #362): a Visa credit card, mortgage,
 * insurance policy or investment product from `customer/products/list`. These
 * products are read-only on the dashboard (no drill-down) — they identify the
 * product for the agent (type, description, identifier and, for a Visa card,
 * the credit limit). The `ProductID` is rendered verbatim: the gateway masks
 * the Visa PAN itself, so no full PAN reaches the DOM.
 */
function ProductTile({
  product,
}: {
  product: DashboardProduct;
}): React.JSX.Element {
  const typeCode = String(product.ProductType ?? '').toUpperCase();
  const label =
    PRODUCT_TYPE_LABEL[typeCode] ?? product.ProductType ?? 'Product';
  // Issue #571 (dylan219): an Active (green) / Inactive (red) status indicator
  // on each "Other products" tile — Active for products returned from
  // `ProductStatus:'A'`, Inactive for those from `ProductStatus:'O'`. Mirrors
  // the account-tile badge; applies to PRODUCTS only.
  const active = isActiveProduct(product);
  const statusLabel = active ? 'Active' : 'Inactive';
  return (
    <article
      className="border-2 border-[#dad7d3] bg-white p-6 shadow-sm relative flex flex-col"
      aria-label={`${label} ${product.ProductID ?? ''}`.trim()}
    >
      <span
        className={`absolute top-6 right-6 px-3 py-1.5 rounded-md text-[13px] font-['Arial',sans-serif] font-bold whitespace-nowrap ${
          active ? 'bg-[#d1fae5] text-[#065f46]' : 'bg-[#fee2e2] text-[#991b1b]'
        }`}
        role="status"
        aria-label={`Product status: ${statusLabel}`}
      >
        {statusLabel}
      </span>
      <div className="mb-4 pr-24">
        <h3 className="text-[22px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-1">
          {label}
        </h3>
        {product.ProductDescription && (
          <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
            {product.ProductDescription}
          </p>
        )}
        <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0 mt-1">
          Product ID:{' '}
          <span className="font-bold text-[#1d252c]">
            {product.ProductID || '—'}
          </span>
        </p>
      </div>

      <div className="border-b-2 border-[#dad7d3] mb-4" aria-hidden="true" />

      <div className="flex flex-col">
        <p className="text-[12px] font-['Arial',sans-serif] text-[#6b7280] uppercase tracking-[0.3px]">
          Limit
        </p>
        <p className="text-[20px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
          {product.ProductLimit
            ? formatCurrency(Number(product.ProductLimit), 'EUR')
            : '—'}
        </p>
      </div>
    </article>
  );
}

/**
 * SCRN 43 — Customer Dashboard (CCF-80 / CCF-43).
 *
 * Faithful port of the customer's Figma "customer dashboard" design: a customer
 * info panel, Quick links / Process menu, and the account/product tiles
 * (current / deposit / loan) with balances and hold indicators.
 *
 * Each tile surfaces the number of signatories required to transact (CCF-80)
 * and the holds on the account (CCF-83): the hold codes show on the tile, and
 * "View Holds" expands the tile inline to reveal the hold + comment (it does
 * not navigate). A secondary link opens the full Account Holds screen (CCF-79)
 * when more detail is needed.
 *
 * Data is fetched from the Teller-ID-gated BFF (`POST /api/customers/dashboard`)
 * using the customer (CIF) and Teller ID held in the customer context after
 * ID&V → Medium Security. The returned customer info enriches the persistent
 * agent banner.
 *
 * CCF-80 tile fields BIC / IBAN / card number / Owners / Signatories are
 * described as OPTIONAL fields on the `Account` schema in `app/openapi.yaml` and
 * read defensively here, rendered only when present — the gateway's
 * `customer/accounts/list` projection may not surface them yet, so they are
 * omitted gracefully and light up automatically when the projection carries
 * them. `CardNumber` is sourced from the gateway's `DebitCardNumber` and shown
 * in FULL plain text on the dashboard tile per the explicit security sign-off
 * scoped to the dashboard page only (dylan219, issue #97).
 *
 * CCF-43 (issue #67): the Customer Authentication Hold Check runs HERE, after
 * authentication, for the ONLINE identification journey only, and FAILS OPEN —
 * see the dedicated effect below.
 */
export function DashboardScreen(): React.JSX.Element {
  const { customer, updateCustomer } = useCustomer();
  const navigate = useNavigate();
  // Shared process-menu navigation handler (issue #45 / #54) — the dashboard
  // uses the same canonical option set and routing as every post-dashboard
  // servicing screen (e.g. Statements), so the menu stays consistent across the
  // app. Each route stays independently RBAC-gated (deny-by-default).
  const onProcessSelect = makeProcessMenuHandler(navigate);
  const [accounts, setAccounts] = useState<DashboardAccount[]>([]);
  // Issue #362: the customer's OTHER products (Visa cards, mortgages, insurance,
  // investments) from `customer/products/list`, rendered as their own tiles.
  const [products, setProducts] = useState<DashboardProduct[]>([]);
  const [info, setInfo] = useState<DashboardInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Issue #672: the accounts leg failed to load (the BFF returned
  // `accountsError: true` because customer/accounts/list could not be confirmed
  // — a gateway business failure or the BAU-SIT 500s, issue #671). The accounts
  // section then shows an inline error + retry rather than a false "No accounts
  // to display" empty state. `info` and `products` still render. `reloadKey`
  // bumps to re-run the loader when the agent hits Retry.
  const [accountsError, setAccountsError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Issue #222 part 2: the servicing dashboard call was refused because the
  // customer is not yet security-verified (server 403). Rather than dead-end on
  // a raw "Forbidden: check Teller ID and permissions." banner — with the agent
  // stranded on a dashboard they should not reach pre-verification — show the
  // "complete security first" panel and route them back to the security step.
  const [verificationRequired, setVerificationRequired] = useState(false);
  // CCF-80: closed/cancelled associations are hidden by default; the agent can
  // opt in to see them appended at the bottom of the list.
  const [showAllAssociations, setShowAllAssociations] = useState(false);

  // CCF-43 (issue #67) — Customer Authentication Hold Check. Non-null ⇒ the
  // customer has an access-restriction hold; the pop-up is shown. The check now
  // runs HERE (on the Dashboard, after authentication) rather than at ID&V, for
  // the ONLINE journey only, and FAILS OPEN: if it cannot return a decision the
  // agent simply continues (no banner, no block).
  const [authHold, setAuthHold] = useState<AuthHoldCheckResult | null>(null);
  const [authHoldOpen, setAuthHoldOpen] = useState(false);
  // Issue #669 (J & N Hold Handling) — `jnHold` (non-null ⇒ at least one account
  // carries BOTH a J and an N hold simultaneously) is derived SYNCHRONOUSLY from
  // the loaded accounts via `useMemo` below, not held in state, so the servicing
  // gate has no transient render window. There is deliberately NO local
  // pop-up state: a found verdict is published to the app-shell navigation-lock
  // store (`jn-hold-block-store.ts`) and App.tsx renders the NON-DISMISSIBLE
  // warning in place of the routed screens on every route — no acknowledge
  // button, no navigating off it (@dylan219, 2026-09-01).
  // CCF-43 — while the hold check is IN FLIGHT for an online/unknown journey,
  // servicing is suppressed so the agent cannot navigate to a servicing screen
  // (and bypass a not-yet-applied AML block) in the race window before the
  // result lands. Starts true for an online/unknown journey (the effect below
  // runs immediately); cleared once the check resolves OR fails open. For the
  // explicit offline journey the check is skipped, so it is never pending.
  const [holdCheckPending, setHoldCheckPending] = useState(
    customer.idvJourney !== 'nsc-account',
  );

  // @dylan219 #784 — the CCF-43 auth-hold + J&N fraud/AML check is ONLINE-ONLY
  // (it protects a live Genesys interaction). The authoritative Online/Offline
  // signal is the server-pinned session `state` from `GET /session`. `null`
  // while it is still resolving (the auth-hold effect keeps servicing pending
  // until it resolves — fail SAFE, don't run servicing before the state is
  // known); `true` = online (run the check); `false` = offline (skip it).
  const [isOnline, setIsOnline] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await api.GET('/session');
        if (cancelled || !data) return;
        if (data.state === 'online' || data.state === 'offline') {
          setIsOnline(data.state === 'online');
        }
      } catch {
        // Leave `isOnline` null on error/until resolved; the auth-hold effect
        // keeps the hold-check pending (servicing suppressed) until it resolves.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';
  const idvJourney = customer.idvJourney;

  // CCF-43 (issue #574) — once the agent acknowledges the auth-hold pop-up for
  // this identified customer, the AUTOMATIC re-pop is suppressed for the rest of
  // the call (Option A, @dylan219). We read the acknowledgement through a REF
  // inside the async hold-check rather than adding it to that effect's
  // dependency array: adding it would re-run the effect (and re-fetch) — the
  // exact stale-closure / effect-race pattern that has bitten this codebase
  // repeatedly (#201/#243/#244/#422). The ref is kept in sync with the context
  // flag by its own effect. The restricted banner + "Review hold details"
  // button do NOT depend on this flag — they always render when a hold exists.
  const authHoldAckRef = useRef(customer.authHoldAcknowledged);
  useEffect(() => {
    authHoldAckRef.current = customer.authHoldAcknowledged;
  }, [customer.authHoldAcknowledged]);

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      if (!customerNumber || !/^\d{4}$/.test(tellerId)) {
        setError('No verified customer in context. Complete ID&V first.');
        return;
      }
      setLoading(true);
      setError('');
      setAccountsError(false);
      setVerificationRequired(false);
      try {
        const { data, response } = await api.POST('/customers/dashboard', {
          // Teller ID remembered from ID&V — mandatory on every call.
          params: { header: { 'X-Teller-Id': tellerId } },
          // Issue #194: "Show all associations" drives the gateway
          // `ShowInactive` flag. The gateway filters closed/cancelled accounts
          // server-side, so the toggle must RE-FETCH with `includeInactive` to
          // reveal them — client-side filtering alone can never show accounts
          // the gateway never returned.
          body: { customerNumber, includeInactive: showAllAssociations },
        });
        if (response.status === 403) {
          // Issue #222 part 2: a 403 here means the customer has not completed
          // security verification (the BFF `requireVerification` gate). Show the
          // "complete security first" panel instead of a raw Forbidden banner.
          if (!cancelled) setVerificationRequired(true);
          return;
        }
        if (!response.ok || !data) {
          if (!cancelled) setError(`Dashboard failed (${response.status}).`);
          return;
        }
        const body = data as unknown as DashboardResponse;
        if (cancelled) return;
        setInfo(body.info);
        setAccounts(body.accounts ?? []);
        // Issue #672: surface a failed accounts leg as an accounts-only error
        // (not a false "no accounts"). `info`/`products` still render.
        setAccountsError(body.accountsError === true);
        setProducts(body.products ?? []);
        // Enrich the persistent banner with the full verification context.
        updateCustomer({
          name: body.info.CustomerName ?? customer.name,
          cif: body.info.CustomerNumber ?? customer.cif,
          dob: body.info.Dob ?? customer.dob,
          segment: body.info.Segment ?? customer.segment,
          // customerVerification (#696): deliberately NOT taken from the
          // dashboard's `customer/info` field. That gateway field is the
          // customer record's STATIC verification flag ('Y'/'N'), a different
          // concept from `customer.customerVerification`, which tracks the
          // AGENT's ID&V PROGRESS — set to 'Y' when the agent completes ID&V
          // (IdvScreen/CustomerSearch/security commits) and read as
          // `priorIdvDone` by the ECL Medium/High security screens. Writing the
          // static record flag here clobbered that progress ('N' for many
          // customers), so after visiting the dashboard ECL High's `priorIdvDone`
          // went false and NONE of its identity-question loads fired — every
          // answer blank (#696). The banner/dashboard verification STATUS is
          // derived from `securityLevel` (see `verification-status.ts` — "not the
          // raw gateway Y/N flag"), so nothing consumes this field from here;
          // preserve the ID&V-progress value rather than overwriting it. Same
          // static-record-vs-live-value rationale as CLI match below.
          assistanceRequired: body.info.AssistanceRequired,
          // CLI match (#36): deliberately NOT taken from the dashboard's
          // `customer/info` field — that flag is a static record value that is
          // not aware of the live caller's CLI. The CLI-match shown in the
          // banner is the value computed during ID&V (card-validate, caller CLI
          // vs registered mobile); preserve it here rather than overwriting it.
          // callsInLastWeek (#709): deliberately NOT taken from here. The PTSB
          // gateway's `customer/info` endpoint never returns a `CallsInLastWeek`
          // field — it is sourced from Genesys analytics data and is set during
          // ID&V screen-pop enrichment (App.tsx). Writing `undefined` here would
          // overwrite the Genesys-sourced value and make the banner show "Not
          // available". Preserve the ID&V-sourced value instead (same pattern as
          // `customerVerification` and `cliMatch` above).
        });
      } catch {
        if (!cancelled) setError('Request failed.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
    // Issue #194: re-fetch when the "Show all associations" toggle changes so the
    // gateway is re-queried with the right `ShowInactive` flag (closed/cancelled
    // accounts are filtered server-side and cannot be revealed client-side).
    // Issue #672: `reloadKey` bumps when the agent hits "Retry" in the accounts
    // error state, re-running the loader (the underlying accounts-list 500s are
    // intermittent, issue #671, so a retry frequently succeeds).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId, showAllAssociations, reloadKey]);

  // CCF-43 (issue #67) — run the Customer Authentication Hold Check on the
  // Dashboard, after authentication. Three deliberate behaviours per the human
  // operator's authoritative instruction on issue #67:
  //   1. ONLINE journey ONLY — skip it entirely for the Sort Code + account
  //      number (offline) identification path;
  //   2. it runs here (Dashboard), not as a gate at ID&V;
  //   3. it FAILS OPEN — if the check cannot be COMPLETED (gateway error / no
  //      response / malformed data) the agent simply continues: no alert, no
  //      warning banner, no block.
  // NB: fail-open applies ONLY to the "cannot complete" case. A returned AML
  // BLOCK is still enforced (the block decision is unchanged) — see
  // `authHoldBlocked` below.
  useEffect(() => {
    // Skip ONLY for the explicit offline (Sort Code + account number) journey.
    // Fail SAFE for a missing/unknown journey: run the check rather than
    // silently skipping an online-card journey when the (client) context field
    // is absent or stale.
    if (idvJourney === 'nsc-account') {
      setHoldCheckPending(false);
      return;
    }
    // No identified customer / teller: the dashboard shows ID&V guidance and
    // there is nothing to check — clear the pending gate so it does not wedge.
    // Checked BEFORE the online gate so a no-context dashboard never wedges on an
    // unresolved session state.
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) {
      setHoldCheckPending(false);
      return;
    }
    // @dylan219 #784 — the CCF-43 auth-hold check is ONLINE-ONLY (it protects a
    // live Genesys interaction). While the session state is still resolving
    // (`isOnline === null`) keep servicing suppressed and wait — do NOT run the
    // check yet (fail SAFE). On an OFFLINE journey (`isOnline === false`) skip
    // the check entirely and lift the pending gate so servicing renders. Only an
    // ONLINE journey (`isOnline === true`) runs the check below.
    if (isOnline === null) {
      setHoldCheckPending(true);
      return;
    }
    if (isOnline === false) {
      setHoldCheckPending(false);
      return;
    }

    let cancelled = false;
    setHoldCheckPending(true);
    async function runAuthHoldCheck(): Promise<void> {
      try {
        // The BFF derives the customer (CIF + name) from the server-side ID&V
        // session; the customerNumber is sent only as a defence-in-depth
        // cross-check (the server rejects a mismatch).
        const { data, error, response } = await api.POST(
          '/idv/auth-hold-check',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled) return;
        // FAIL OPEN: on any non-success response, error body, or malformed
        // result the agent simply continues — nothing is surfaced. Only a
        // well-formed result that carries an actual hold raises the pop-up.
        if (
          error ||
          !response.ok ||
          !data ||
          typeof data.blocked !== 'boolean' ||
          typeof data.hasHold !== 'boolean'
        ) {
          return;
        }
        if (data.hasHold) {
          // Always SET the hold so the persistent restricted banner + the
          // on-demand "Review hold details" button keep working. Only AUTO-open
          // the pop-up when the agent has not already acknowledged it for this
          // customer on this call (CCF-43 / issue #574). Read the flag through
          // the ref to avoid re-running (and re-fetching) this effect.
          setAuthHold(data as AuthHoldCheckResult);
          if (!authHoldAckRef.current) setAuthHoldOpen(true);
        }
      } catch {
        // FAIL OPEN: a network/transport failure must not block or warn — the
        // agent continues (issue #67).
      } finally {
        // The check has resolved (hold raised) or reached the sanctioned
        // fail-open outcome — lift the pending gate so servicing renders. A
        // returned BLOCK keeps servicing suppressed via `authHoldBlocked`.
        if (!cancelled) setHoldCheckPending(false);
      }
    }

    void runAuthHoldCheck();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId, idvJourney, isOnline]);

  // Issue #669 (J & N Hold Handling) — whenever an account carries BOTH a J
  // ("Multiple Warnings") and an N ("Pay No Withdrawal") hold simultaneously the
  // agent cannot proceed and must transfer the customer to the fraud team.
  //
  // The verdict is derived SYNCHRONOUSLY (useMemo) from the holds already loaded
  // in the accounts projection — no extra gateway call, no contract change, and
  // crucially NO transient render window: `servicingVisible` below reads this
  // memo in the SAME render the accounts land in, so the servicing tiles/links
  // are never briefly shown for a J+N account before a `useState` catches up
  // (ai-review #669). This is only the UX gate; the authoritative control is the
  // BFF `requireNoJnHoldBlock` guard, which 403s the servicing/banking routes
  // server-side regardless of what the SPA renders.
  const jnHold = useMemo<JnHoldResult | null>(() => {
    const customerName = info?.CustomerName ?? customer.name;
    return evaluateJnHold(
      accounts.map((a) => ({
        accountNumber: a.AccountNumber,
        holdCodes: (a.Holds ?? [])
          .map((h) => h.Code)
          .filter((c): c is string => typeof c === 'string' && c.length > 0),
      })),
      customerName,
    );
  }, [accounts, info, customer.name]);

  // Issue #669 (@dylan219, 2026-09-01): publish the J&N verdict to the app-shell
  // navigation-lock store. A FOUND verdict makes App.tsx replace the routed
  // screens with the NON-DISMISSIBLE warning on every route — the agent cannot
  // acknowledge it or navigate off it. A cleared verdict (accounts re-read
  // without the J+N combination) drops the lock, but only when the active block
  // belongs to THIS customer (a block for another customer is never touched).
  // The dashboard deliberately does NOT clear the lock on unmount: the lock
  // itself unmounts this screen while it is active, and the block must persist
  // until the identity changes (new ID&V / Clear Session — the same moments the
  // server drops its `requireNoJnHoldBlock`).
  // @dylan219 #784 — the J&N fraud/AML stop is ONLINE-ONLY too: publish the block
  // only on a live (online) interaction. On a CONFIRMED OFFLINE journey there is
  // no live call to protect, so drop any existing block for THIS customer. The
  // fail-closed J&N enforcement still fully applies ONLINE.
  //
  // Three states, handled distinctly (ai-review #784 HIGH — do NOT collapse the
  // indeterminate case into "clear"):
  //   - `isOnline === true` + a J&N verdict → PUBLISH the block.
  //   - `isOnline === true` + no verdict, OR `isOnline === false` (confirmed
  //     offline) → CLEAR any stale block for THIS customer (a determinate "no
  //     block applies" verdict).
  //   - `isOnline === null` (INDETERMINATE — `GET /session` still resolving or
  //     failed) → LEAVE any existing block intact. Clearing here would be
  //     fail-OPEN: a previously-published block for this customer could be
  //     dropped before the app has confirmed the journey is actually offline.
  useEffect(() => {
    if (!customerNumber) return;
    if (isOnline === true && jnHold) {
      setJnHoldBlock({ customerNumber, result: jnHold });
    } else if (
      isOnline !== null &&
      getJnHoldBlock()?.customerNumber === customerNumber
    ) {
      setJnHoldBlock(null);
    }
  }, [customerNumber, jnHold, isOnline]);

  // CCF-80: default view = all statuses except Closed/Cancelled. With "Show all
  // associations" ticked, the open accounts come first and the closed/cancelled
  // ones are appended (combined) at the bottom of the list.
  //
  // Issue #110: within each (active / non-active) set the tiles are
  // grouped/ordered by gateway ProductType in the fixed PTSB sequence — Current
  // (CAA) → Savings (SBA) → Deposit (TDA) → Term Loans (LAA) → Mortgages (HLA &
  // PEP) → Credit Cards (CC) — preserving the gateway's order within a group. The
  // active/non-active split is preserved (non-active accounts sort to the
  // bottom).
  //
  // Issue #420: the default view is ACTIVE-only (Dylan's authoritative ruling);
  // every non-Active association (Inactive / Dormant / Closed / Cancelled) is
  // grouped below and shown only once the "Show all associations" toggle is
  // ticked.
  const activeAccounts = orderByProductType(accounts.filter(isActiveAccount));
  const otherAccounts = orderByProductType(
    accounts.filter((a) => !isActiveAccount(a)),
  );
  const displayedAccounts = showAllAssociations
    ? [...activeAccounts, ...otherAccounts]
    : activeAccounts;

  // Issue #362 / #379: the customer's other products, filtered to Visa cards,
  // mortgages, Insurance and Investment (only Customer Messages are hidden) and
  // ordered per PRODUCT_TILE_ORDER, rendered as their own tiles below the
  // accounts.
  const displayedProducts = orderProducts(products);

  // CCF-43 — a returned AML hold on a sole/all-account basis BLOCKS servicing.
  // The block decision for a returned hold is UNCHANGED (issue #67 relaxed only
  // the cannot-complete behaviour, to fail open). When blocked, the dashboard
  // enforces a hard restricted state: the account tiles + quick links are
  // suppressed and a persistent, non-dismissible restricted banner is shown
  // (re-opening the hold details on demand). The agent cannot service the
  // customer until the hold is lifted.
  const authHoldBlocked = authHold?.blocked === true;
  // Issue #669: a J-and-N hold on any account is a fraud/AML stop condition —
  // the agent cannot proceed and must transfer to the fraud team, so servicing
  // is suppressed exactly like a blocking AML hold.
  // @dylan219 #784: the J&N fraud/AML stop is ONLINE-ONLY (it protects a live
  // Genesys interaction), so it suppresses servicing only when the session is
  // ONLINE. On an OFFLINE journey (or while the session state is still
  // resolving) there is no live call to protect and the block does not apply —
  // servicing is not suppressed by a J&N verdict. This mirrors the online-only
  // gate on the app-shell publish effect above.
  const jnHoldBlocked = isOnline === true && jnHold !== null;
  // Servicing (account tiles + quick links + associations) is shown only once
  // the hold check has resolved (or failed open) AND the result is not a block.
  // Suppressing it while pending closes the race where a blocking AML hold has
  // not yet been applied (the agent could otherwise click into a servicing
  // screen before the async check returns).
  const servicingVisible =
    !authHoldBlocked && !jnHoldBlocked && !holdCheckPending;

  // Issue #222 part 2: pre-verification the servicing dashboard call is refused
  // (403) — render the "complete security first" panel + route back to security
  // rather than the raw Forbidden banner / an empty dashboard.
  if (verificationRequired) {
    return <ServicingVerificationRequired />;
  }

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="dashboard-heading"
    >
      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="dashboard-heading"
          className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0"
        >
          Customer dashboard
        </h1>
        <ProcessMenu
          options={PROCESS_MENU_OPTIONS}
          onSelect={onProcessSelect}
        />
      </div>

      {error && (
        <div
          role="alert"
          className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
        >
          {error}
        </div>
      )}

      {/* CCF-43 — persistent BLOCK banner. A returned AML hold on a sole/all-
          account basis restricts access; the block decision is unchanged (issue
          #67 relaxed only the cannot-complete behaviour). The agent cannot
          service the customer — the tiles + quick links are suppressed below —
          and the hold details can be re-opened on demand. */}
      {authHoldBlocked && (
        <div
          role="alert"
          aria-live="assertive"
          className="mb-8 p-4 border border-[#dc2626] bg-white flex items-start gap-3"
        >
          <div className="flex-1 space-y-2">
            <h2 className="text-[18px] font-['Arial',sans-serif] font-bold text-[#dc2626] m-0">
              Account access restricted
            </h2>
            <p className="text-[14px] font-['Arial',sans-serif] text-[#1D252C] m-0 leading-[1.5]">
              This customer has an account hold that restricts access. You
              cannot proceed with servicing — follow the scripting shown.
            </p>
            <Button
              type="button"
              onClick={() => setAuthHoldOpen(true)}
              aria-label="Review authentication hold details"
              className="bg-white text-[#1d252c] border-2 border-[#1d252c] rounded-md px-4 py-2 h-auto font-['Arial',sans-serif] font-bold text-[14px]"
            >
              Review hold details
            </Button>
          </div>
        </div>
      )}

      {/* Issue #669 (J & N Hold Handling) — a persistent, non-dismissible
          restricted banner shown whenever an account carries BOTH a J and an N
          hold simultaneously. Servicing (tiles + quick links) is suppressed.
          (The former "Review hold details" button re-opened the dismissible
          pop-up; the pop-up is now the app-shell navigation lock's
          NON-DISMISSIBLE warning — always on screen while blocked, so there is
          nothing left to re-open — @dylan219, 2026-09-01.) */}
      {jnHoldBlocked && (
        <div
          role="alert"
          aria-live="assertive"
          className="mb-8 p-4 border border-[#dc2626] bg-white flex items-start gap-3"
        >
          <div className="flex-1 space-y-2">
            <h2 className="text-[18px] font-['Arial',sans-serif] font-bold text-[#dc2626] m-0">
              Account status restricted — transfer to fraud team
            </h2>
            <p className="text-[14px] font-['Arial',sans-serif] text-[#1D252C] m-0 leading-[1.5]">
              This account has both a J and an N hold in place. You cannot
              proceed with servicing — transfer the customer to the fraud team
              and follow the scripting shown.
            </p>
          </div>
        </div>
      )}

      <div className="mb-8 flex flex-col lg:flex-row gap-6">
        {/* Customer information */}
        <div className="w-full lg:w-[480px] border-2 border-[#dad7d3] bg-white shadow-sm p-8 shrink-0">
          <h2 className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-6 m-0">
            Customer information
          </h2>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0">
                Full name
              </p>
              <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                {info?.CustomerName ?? customer.name ?? '—'}
              </p>
            </div>
            <div>
              <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0">
                Customer type
              </p>
              <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                {/* CCF-80 / #770: customer type is the raw CustDescription from customer/identification, not the normalised routing Segment. */}
                {info?.CustomerType ?? '—'}
              </p>
            </div>
            <div>
              <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0">
                Compliance Info
              </p>
              <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                {/* CCF-80: collapsed compliance status (ML + EU) from the BFF. */}
                {info?.CustomerCompliance ?? '—'}
              </p>
            </div>
            <div>
              <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0">
                Date of Birth
              </p>
              <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                {/* CCF-80: DOB formatted DD/MM/YYYY (e.g. 20/04/1984). */}
                {info?.Dob ? formatDob(info.Dob) : '—'}
              </p>
            </div>
          </div>
        </div>

        {/* Quick links — suppressed while the hold check is pending or an AML
            hold blocks servicing. */}
        {servicingVisible && (
          <div className="flex-1 border-2 border-[#dad7d3] bg-white shadow-sm p-8">
            <h2 className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-6 m-0">
              Quick links
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {QUICK_LINKS.map((link) => (
                <button
                  key={link}
                  type="button"
                  onClick={() => {
                    if (link === 'Card maintenance') navigate('/cards');
                    else if (link === 'Payments') navigate('/payments');
                    else if (link === 'Statements') navigate('/statements');
                    else if (link === 'Transaction history')
                      navigate('/transactions');
                    else if (link === 'Interaction history')
                      // Quick link → Call History (SCRN-13, CCF-3): the
                      // interaction-journey record for this customer.
                      navigate('/call-history');
                    else if (link === 'Change Customer Details')
                      // Quick link → Change Customer Details (SCRN-24), the same
                      // destination and label as the "Change Customer Details"
                      // process-menu item (CCF-80: quick links go directly to
                      // the process; #620: label aligned with the Process Menu).
                      navigate('/personal-details');
                    else toast.info(link);
                  }}
                  aria-label={link}
                  className="relative pl-4 pr-4 py-2.5 bg-[#f3f4f6] text-[#D23A06] font-['Arial',sans-serif] font-bold text-[16px] transition-all focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc]/20 hover:bg-[#e5e7eb] text-left overflow-hidden"
                >
                  <span
                    className="absolute left-0 top-0 h-full w-[4px] bg-[#D23A06]"
                    aria-hidden="true"
                  />
                  <span className="relative z-10">{link}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* While the hold check is still in flight, suppress servicing and show a
          checking placeholder so the agent cannot navigate into a servicing
          screen before a (possible) block is applied. */}
      {holdCheckPending && !authHoldBlocked && (
        <p
          role="status"
          className="font-['Arial',sans-serif] text-[16px] text-[#1d252c]"
        >
          Checking account holds…
        </p>
      )}

      {/* Servicing — accounts + associations. Suppressed entirely while the hold
          check is pending or an AML hold blocks access (no servicing). */}
      {servicingVisible && (
        <>
          {/* Show all associations — reveals Closed/Cancelled accounts (CCF-80). */}
          <div className="mb-6 flex items-center">
            <input
              type="checkbox"
              id="show-all-associations"
              checked={showAllAssociations}
              onChange={(e) => setShowAllAssociations(e.target.checked)}
              className="w-[20px] h-[20px] rounded-[4px] border-2 border-[#979c9f] accent-[#1d252c] cursor-pointer focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-[#005fcc]"
            />
            <label
              htmlFor="show-all-associations"
              className="ml-3 text-[16px] font-['Arial',sans-serif] text-[#1d252c] select-none cursor-pointer"
            >
              Show all associations
            </label>
          </div>

          {/* Account tiles */}
          <section aria-label="Customer accounts">
            <h2 className="sr-only">Customer accounts</h2>
            {loading ? (
              <p
                role="status"
                className="font-['Arial',sans-serif] text-[16px] text-[#1d252c]"
              >
                Loading accounts…
              </p>
            ) : accountsError ? (
              // Issue #672: the accounts leg could NOT be confirmed (BFF
              // `accountsError: true`). Show an inline error + retry — NEVER a
              // "No accounts to display" empty state, which would misrepresent a
              // customer who has accounts as having none. The underlying cause
              // is the intermittent BAU-SIT gateway 500s (issue #671), so a
              // retry usually succeeds.
              <div
                role="alert"
                className="max-w-[720px] rounded-[8px] border border-[#c8102e] bg-[#fdeaec] p-4 font-['Arial',sans-serif] text-[16px] text-[#1d252c]"
              >
                <p className="font-bold">Accounts could not be loaded.</p>
                <p className="mt-1">
                  The customer&rsquo;s accounts did not load. This is a
                  temporary problem retrieving the account list — the
                  customer&rsquo;s other details are shown below. Please try
                  again.
                </p>
                <button
                  type="button"
                  onClick={() => setReloadKey((k) => k + 1)}
                  className="mt-3 rounded-[4px] border border-[#c8102e] bg-white px-4 py-2 font-bold text-[#c8102e] hover:bg-[#c8102e] hover:text-white"
                >
                  Retry
                </button>
              </div>
            ) : displayedAccounts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 max-w-[1400px]">
                {displayedAccounts.map((account) => (
                  <AccountTile
                    key={account.AccountNumber}
                    account={account}
                    onOpen={(a) => {
                      // Credit-card tiles have no drill-down (issue #75) — the
                      // tile suppresses its click target, but guard here too so
                      // a stray invocation is a safe no-op rather than a nav to
                      // "null?account=…".
                      const path = detailPathFor(a.AccountType);
                      if (path === null) return;
                      navigate(
                        `${path}?account=${encodeURIComponent(a.AccountNumber)}`,
                      );
                    }}
                    onViewHolds={(a) =>
                      navigate(
                        `/accounts/holds?account=${encodeURIComponent(a.AccountNumber)}`,
                      )
                    }
                  />
                ))}
              </div>
            ) : (
              !error && (
                <p className="font-['Arial',sans-serif] text-[16px] text-[#6b7280]">
                  No accounts to display.
                </p>
              )
            )}
          </section>

          {/* Other products — Visa cards, mortgages, Insurance and Investment
              (issue #379; only Customer Messages are filtered out), sourced from
              `customer/products/list`. These do NOT appear on
              `customer/accounts/list`, so they are their own tiles. Rendered
              only when the customer holds such products. */}
          {displayedProducts.length > 0 && (
            <section aria-label="Customer products" className="mt-10">
              <h2 className="text-[22px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-6">
                Other products
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 max-w-[1400px]">
                {displayedProducts.map((product, i) => (
                  <ProductTile
                    key={`${product.ProductType ?? 'p'}-${product.ProductID ?? i}`}
                    product={product}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Re-run / refresh action. */}
          <div className="mt-8">
            <Button
              type="button"
              onClick={() => window.location.reload()}
              aria-label="Refresh dashboard"
              className="bg-white text-[#1d252c] border-2 border-[#1d252c] hover:bg-[#f3f4f6] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
            >
              Refresh
            </Button>
          </div>
        </>
      )}

      {/* CCF-43 (issue #67) — Customer Authentication Hold Check pop-up. Runs on
          the Dashboard for the ONLINE journey only. A NON-blocking hold (fraud /
          one-of-several accounts) is informational — the agent acknowledges and
          the dashboard remains usable. A BLOCKING AML hold suppresses servicing
          (tiles + quick links) and shows the persistent restricted banner above;
          acknowledging the pop-up does not restore access. The check fails OPEN
          (no pop-up) when it cannot be completed. */}
      {authHold && authHoldOpen && (
        <AuthHoldPopup
          result={authHold}
          onClose={() => {
            setAuthHoldOpen(false);
            // CCF-43 (issue #574): remember the acknowledgement so the pop-up is
            // not auto-re-raised for this customer for the rest of the call. The
            // restricted banner + "Review hold details" button remain.
            updateCustomer({ authHoldAcknowledged: true });
          }}
        />
      )}

      {/* Issue #669 (J & N Hold Handling) — the fraud-team-transfer warning is
          NO LONGER a local pop-up here: the synchronous `jnHold` verdict above
          is published to the app-shell navigation-lock store, and App.tsx
          renders the NON-DISMISSIBLE warning IN PLACE OF the routed screens on
          every route (@dylan219, 2026-09-01: the "Acknowledge — cannot proceed"
          button is removed and the agent cannot navigate off the warning while
          the J&N block is up). The server-side `requireNoJnHoldBlock` 403
          freeze remains the backstop. */}
    </main>
  );
}
